/**
 * PV Validation Helper Functions
 * Extracted from LoadPV component for testability
 */
import { mgaRegex } from "../../constants/helper";
import { formatProjectNumberRange } from "../../utils/helpers";

const range = (min, max, numberFormat) =>
  formatProjectNumberRange(min, max, numberFormat);

const isValueMissing = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const isZeroLike = (value) => !isValueMissing(value) && Number(value) === 0;

// Profile Detail validators
export const validateProfileStartDate = (value, isUploadPV, t) =>
  isUploadPV && (value ? null : t("pv.startDate.required"));

export const validateProfileStartTime = (value, isUploadPV, t) =>
  isUploadPV && (value ? null : t("pv.startTime.required"));

export const validateProfileTimeResolution = (value, isUploadPV, t) =>
  isUploadPV && (value ? null : t("pv.timeResolution.required"));

export const validateProfileUnits = (value, isUploadPV, t) =>
  isUploadPV && (value ? null : t("pv.units.required"));

// Component Attribute validators
export const validatePowNomRef = (value, t, relACPower, isUploadPv, numberFormat) =>
  value === 0
    ? null
    : !value
    ? t("pv.validation.nominalAcPowerRequired")
    : !mgaRegex?.ZeroPointOneTO99999WithSingleDoubleDecimal?.test(Number(value))
    ? t("pv.validation.nominalAcPowerInvalid", range(0.1, 99999, numberFormat))
    : (!isUploadPv && Number(value) > Number(relACPower) * 1.5)
    ? t("pv.validation.nominalAcPowerExceedsRatedAcPower")
    : null;

export const validateFile = (value, isUploadPV, isUploadPvFileButtonClicked, profileFileLength, t) =>
  isUploadPV &&
  (value && isUploadPvFileButtonClicked
    ? t("pv.clickGetProfile")
    : profileFileLength || value
    ? null
    : t("pv.file.required"));

export const validateRelDCPower = (value, isUploadPV, t, numberFormat) =>
  !isUploadPV &&
  (!value
    ? t("pv.validation.ratedDcPowerRequired")
    : !mgaRegex?.ZeroPointOneTO99999WithSingleDoubleDecimal?.test(Number(value))
    ? t("pv.validation.ratedDcPowerInvalid", range(0.1, 99999, numberFormat))
    : null);

export const validateInvEfficiency = (value, isUploadPV, t, numberFormat) =>
  !isUploadPV &&
  (!value
    ? t("pv.validation.inverterEfficiencyRequired")
    : !mgaRegex?.FiftyToHundred?.test(Number(value))
    ? t("pv.validation.inverterEfficiencyInvalid", range(50, 100, numberFormat))
    : null);

export const validateRelACPower = (value, isUploadPV, t, numberFormat) =>
  !isUploadPV &&
  (!value
    ? t("pv.validation.ratedAcPowerRequired")
    : !mgaRegex?.ZeroPointOneTO99999WithSingleDoubleDecimal?.test(Number(value))
    ? t("pv.validation.ratedAcPowerInvalid", range(0.1, 99999, numberFormat))
    : null);

export const validateTilt = (value, isUploadPV, t, numberFormat) =>
  !isUploadPV &&
  (isValueMissing(value)
    ? t("pv.validation.tiltAngleRequired")
    : isZeroLike(value)
    ? null
    : !mgaRegex?.ZeroToNinetyWithOneDecimal?.test(Number(value))
    ? t("pv.validation.tiltAngleInvalid", range(0, 90, numberFormat))
    : null);

export const validateOrientation = (value, isUploadPV, t, numberFormat) =>
  !isUploadPV &&
  (isValueMissing(value)
    ? t("pv.validation.orientationRequired")
    : isZeroLike(value)
    ? null
    : !mgaRegex?.zeroToThreeSixtyWithDecimal?.test(String(value))
    ? t("pv.validation.orientationInvalid", range(0, 360, numberFormat))
    : null);

export const validateSystemLoss = (value, isUploadPV, t, numberFormat) =>
  !isUploadPV &&
  (isValueMissing(value)
    ? t("pv.validation.systemLossRequired")
    : isZeroLike(value)
    ? null
    : !mgaRegex?.ZeroToTwentyWithDecimal?.test(Number(value))
    ? t("pv.validation.systemLossInvalid", range(0, 20, numberFormat))
    : null);

export const validateAxisType = (value, isUploadPV, t) =>
  !isUploadPV &&
  (isValueMissing(value) ? t("pv.validation.axisTypeRequired") : null);

export const validateCreateProfileButtonClicked = (
  isUploadPV,
  createProfileDataLength,
  createProfileButtonClicked,
  t
) =>
  !isUploadPV &&
  (createProfileDataLength
    ? null
    : !createProfileButtonClicked
    ? t("pv.clickGetProfile")
    : null);

// Economic Attribute validators
export const validatePvCapexCost = (value, pvSetup, t, numberFormat) =>
  pvSetup === "new" &&
  (value === 0
    ? null
    : !value
    ? t("pv.validation.pvCapexCostRequired")
    : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(value))
    ? t("pv.validation.pvCapexCostInvalid", range(0, 1000000000, numberFormat))
    : null);

export const validateInvLifetime = (value, t, numberFormat) =>
  isValueMissing(value)
    ? t("pv.validation.invLifetimeRequired")
    : !mgaRegex?.oneTo25Regex?.test(String(value).trim())
    ? t("pv.validation.invLifetimeInvalid", range(1, 25, numberFormat))
    : null;

export const validatePvReplacementCost = (value, t, numberFormat) =>
  isValueMissing(value)
    ? t("pv.validation.pvReplacementCostRequired")
    : value === 0
    ? null
    : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(value))
    ? t("pv.validation.pvReplacementCostInvalid", range(0, 1000000000, numberFormat))
    : null;

export const validatePvOmCost = (value, t, numberFormat) =>
  value === 0
    ? null
    : !value
    ? t("pv.validation.pvOmCostRequired")
    : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(value))
    ? t("pv.validation.pvOmCostInvalid", range(0, 1000000000, numberFormat))
    : null;

export const validatePvLifetime = (value, t, numberFormat) =>
  isValueMissing(value)
    ? t("pv.validation.pvLifetimeRequired")
    : !mgaRegex?.oneTo25Regex?.test(String(value).trim())
    ? t("pv.validation.pvLifetimeInvalid", range(1, 25, numberFormat))
    : null;

export const validateInvOmCost = (value, t, numberFormat) =>
  value === 0
    ? null
    : !value
    ? t("pv.validation.invOmCostRequired")
    : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(value))
    ? t("pv.validation.invOmCostInvalid", range(0, 1000000000, numberFormat))
    : null;

export const validateInvCapexCost = (value, pvSetup, t, numberFormat) =>
  pvSetup === "new" &&
  (value === 0
    ? null
    : !value
    ? t("pv.validation.invCapexCostRequired")
    : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(value))
    ? t("pv.validation.invCapexCostInvalid", range(0, 1000000000, numberFormat))
    : null);

// Helper functions (pure)
export const computeNominalAcPower = (createdProfile = []) => {
  if (!Array.isArray(createdProfile) || createdProfile.length === 0) {
    return 0;
  }
  const minVal = Math.min(...createdProfile);
  const positive = Math.abs(minVal || 0);
  return positive;
};

export const computeRelACPower = (invEfficiency, relDCPower) => {
  const hasInv = invEfficiency !== undefined && invEfficiency !== null && invEfficiency !== "";
  const hasDC = relDCPower !== undefined && relDCPower !== null && relDCPower !== "";
  if (hasInv && hasDC) {
    const product = (Number(invEfficiency) / 100) * Number(relDCPower);
    return Number.isNaN(product) ? 0 : product.toFixed(2);
  }
  if (hasInv) return Number(invEfficiency) || 0;
  if (hasDC) return Number(relDCPower) || 0;
  return 0;
};
